import { INDUSTRY_BLUEPRINTS, blueprintSummary } from '@shared/blueprint'
import { getIndustry } from '@shared/catalog'
import type { IndustryId } from '@shared/types'
import { Card, Chip } from '../ui'

function formatSla(minutes: number): string {
  if (minutes < 60) return `${minutes} min`
  const hours = minutes / 60
  return hours === 1 ? '1 hour' : `${hours % 1 === 0 ? hours : hours.toFixed(1)} hours`
}

/**
 * Shows what picking an industry actually configures, before the customer commits.
 *
 * Setup wizards usually ask for a sector and then appear to do nothing with the answer.
 * Naming the labels, fields and replies that will be created makes the choice legible, and
 * makes it obvious that switching to a different sector would produce a different workspace.
 */
export function BlueprintPreview({ industryId }: { industryId: IndustryId }) {
  const blueprint = INDUSTRY_BLUEPRINTS[industryId]
  const industry = getIndustry(industryId)
  const summary = blueprintSummary(industryId)
  if (!industry) return null

  return (
    <Card variant="outlined">
      <div className="stack gap-4">
        <div className="stack gap-2">
          <span className="cad-eyebrow">What we will set up</span>
          <h3 className="cad-section-title">{industry.name} workspace</h3>
          <p className="cad-muted" style={{ maxWidth: '62ch' }}>
            {blueprint.rationale}
          </p>
        </div>

        <div className="row gap-2 wrap">
          <Chip tone="assist">{summary.labels} labels</Chip>
          <Chip tone="assist">{summary.rules} routing rules</Chip>
          <Chip tone="assist">
            {summary.chatProperties + summary.ticketProperties} custom fields
          </Chip>
          <Chip tone="assist">{summary.quickReplies} quick replies</Chip>
          <Chip tone="assist">{summary.knowledgeAnswers} starter FAQ answers</Chip>
          <Chip tone={summary.tightestSlaMinutes <= 15 ? 'warn' : 'assist'}>
            Fastest reply target {formatSla(summary.tightestSlaMinutes)}
          </Chip>
        </div>

        <div className="blueprint-grid">
          <section className="stack gap-2">
            <span className="cad-label">Labels</span>
            <div className="row gap-2 wrap">
              {blueprint.labels.map((label) => (
                <span key={label.name} className="label-swatch">
                  <span className="label-dot" style={{ background: label.color }} aria-hidden="true" />
                  {label.name}
                </span>
              ))}
            </div>
            <p className="cad-muted">
              Labels also decide what each teammate can see, so an agent given only one label
              sees only those chats.
            </p>
          </section>

          <section className="stack gap-2">
            <span className="cad-label">Fields on every chat</span>
            <ul className="plain-list body-small">
              {blueprint.chatProperties.map((property) => (
                <li key={property.name}>
                  {property.name}
                  <span className="cad-muted"> · {property.type.replace('_', ' ')}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="stack gap-2">
            <span className="cad-label">Fields on a ticket</span>
            <ul className="plain-list body-small">
              {blueprint.ticketProperties.map((property) => (
                <li key={property.name}>
                  {property.name}
                  {property.requiredBeforeClose && (
                    <span className="cad-muted"> · required to close</span>
                  )}
                </li>
              ))}
            </ul>
            {summary.mandatoryTicketFields > 0 && (
              <p className="cad-muted">
                {summary.mandatoryTicketFields} of these must be filled before a ticket can be
                closed, so the record exists when someone asks for it later.
              </p>
            )}
          </section>

          <section className="stack gap-2">
            <span className="cad-label">Quick replies</span>
            <ul className="plain-list body-small">
              {blueprint.quickReplies.slice(0, 4).map((reply) => (
                <li key={reply.shortcut}>
                  <code>{reply.shortcut}</code>
                  <span className="cad-muted"> {reply.title}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="stack gap-2">
            <span className="cad-label">Starter FAQ answers</span>
            <ul className="plain-list body-small">
              {blueprint.knowledgeAnswers.map((answer) => (
                <li key={answer.title}>{answer.title}</li>
              ))}
            </ul>
            <p className="cad-muted">
              Generic on purpose — worth a quick edit once you're in, but auto-reply and the
              knowledge screen have something to work with from the first message.
            </p>
          </section>
        </div>

        <p className="cad-muted">
          Everything here is editable once you are in. It is a starting point, not a lock-in.
        </p>
      </div>
    </Card>
  )
}
